import { Mutex } from 'async-mutex';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { useUpdateCachedReflectionTopicCompletionStatusCallback } from '../../hooks/reflections/useUpdateCachedReflectionTopicCompletionStatusCallback';
import { disarmNavigationGuard } from '../../session/hooks/useNavigationGuard';
import { DataChannelMessage, DataChannelMessageType } from '../../video-sdk';
import { isRestartingSessionAtom } from '../state';
import { useHandleReceivedSessionIntentionPromptCallback } from './useHandleReceivedSessionIntentionPrompt';
import { useHandleReceivedSessionIntentionUpdatedCallback } from './useHandleReceivedSessionIntentionUpdatedCallback';
import { useUpdateDataChannelAppStateCallback } from './useUpdateDataChannelAppStateCallback';

const handleReceivedMessageMutex = new Mutex();

/**
 * Returns a callback that handles received messages on the data track for the
 * client
 */
export function useHandleReceivedMessageCallback() {
  const updateDataTrackState = useUpdateDataChannelAppStateCallback();
  const updateCachedTopicCompletionStatus = useUpdateCachedReflectionTopicCompletionStatusCallback();
  const setIsRestartingSession = useSetAtom(isRestartingSessionAtom);
  const handleReceivedSessionIntentionUpdatedCallback = useHandleReceivedSessionIntentionUpdatedCallback();
  const handleReceivedSessionIntentionPrompt = useHandleReceivedSessionIntentionPromptCallback();

  return useCallback(
    (message: DataChannelMessage) => {
      return handleReceivedMessageMutex.runExclusive(async () => {
        switch (message.type) {
          case DataChannelMessageType.APP_STATE:
            updateDataTrackState(message);
            break;
          case DataChannelMessageType.REFLECTION_TOPIC_UPDATE:
            updateCachedTopicCompletionStatus(message.topic);
            break;
          case DataChannelMessageType.RESTART_SESSION:
            setIsRestartingSession(true);
            disarmNavigationGuard();
            window.location.reload();
            break;
          case DataChannelMessageType.SESSION_INTENTION_SET:
            handleReceivedSessionIntentionUpdatedCallback(
              DataChannelMessageType.SESSION_INTENTION_SET,
              message.sessionIntention.shortId,
            );
            break;
          case DataChannelMessageType.SESSION_INTENTION_EDITED:
            handleReceivedSessionIntentionUpdatedCallback(
              DataChannelMessageType.SESSION_INTENTION_EDITED,
              message.sessionIntention.shortId,
            );
            break;
          case DataChannelMessageType.SESSION_INTENTION_PROMPT:
            handleReceivedSessionIntentionPrompt(message);
            break;
        }
      });
    },
    [
      setIsRestartingSession,
      updateCachedTopicCompletionStatus,
      updateDataTrackState,
      handleReceivedSessionIntentionUpdatedCallback,
      handleReceivedSessionIntentionPrompt,
    ],
  );
}
